import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';

export default function AdminsPage() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'admin',
    is_active: true,
    otp_enabled: false
  });
  const [editingId, setEditingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admins');
      setAdmins(res.data);
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error cargando administradores', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const handleOpenForm = admin => {
    if (admin) {
      setEditingId(admin.id);
      setFormData({
        username: admin.username,
        email: admin.email,
        full_name: admin.full_name,
        password: '',
        role: admin.role,
        is_active: admin.is_active,
        otp_enabled: admin.otp_enabled
      });
    } else {
      setEditingId(null);
      setFormData({ username: '', email: '', full_name: '', password: '', role: 'admin', is_active: true, otp_enabled: false });
    }
    setOpenForm(true);
  };

  const handleCloseForm = () => setOpenForm(false);

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await api.put(`/admins/${editingId}`, payload);
        setSnackbar({ open: true, message: 'Administrador actualizado', severity: 'success' });
      } else {
        await api.post('/admins', formData);
        setSnackbar({ open: true, message: 'Administrador creado', severity: 'success' });
      }
      handleCloseForm();
      fetchAdmins();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error guardando administrador', severity: 'error' });
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar administrador?')) return;
    try {
      await api.delete(`/admins/${id}`);
      setSnackbar({ open: true, message: 'Administrador eliminado', severity: 'info' });
      fetchAdmins();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error al eliminar', severity: 'error' });
    }
  };

  const toggleOtp = async (id, enable) => {
    try {
      await api.post(`/admins/${id}/${enable ? 'activate-otp' : 'deactivate-otp'}`);
      setSnackbar({ open: true, message: `OTP ${enable ? 'activado' : 'desactivado'}`, severity: 'success' });
      fetchAdmins();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Error cambiando OTP', severity: 'error' });
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Administrar Admins</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenForm(null)}>Nuevo Admin</Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>
      ) : (
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Activo</TableCell>
                <TableCell>OTP</TableCell>
                <TableCell>Creado</TableCell>
                <TableCell>Último Login</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {admins.map(a => (
                <TableRow key={a.id} hover>
                  <TableCell>{a.id}</TableCell>
                  <TableCell>{a.username}</TableCell>
                  <TableCell>{a.email}</TableCell>
                  <TableCell>{a.full_name}</TableCell>
                  <TableCell>{a.role}</TableCell>
                  <TableCell>{a.is_active ? 'Sí' : 'No'}</TableCell>
                  <TableCell>{a.otp_enabled ? 'Sí' : 'No'}</TableCell>
                  <TableCell>{new Date(a.created_at).toLocaleString()}</TableCell>
                  <TableCell>{a.last_login ? new Date(a.last_login).toLocaleString() : '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenForm(a)}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(a.id)}><DeleteIcon /></IconButton>
                    <Button size="small" onClick={() => toggleOtp(a.id, !a.otp_enabled)}>OTP {a.otp_enabled ? 'Off' : 'On'}</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Editar Admin' : 'Nuevo Admin'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Usuario" name="username" value={formData.username} onChange={handleChange} />
          <TextField fullWidth margin="normal" label="Email" name="email" value={formData.email} onChange={handleChange} />
          <TextField fullWidth margin="normal" label="Nombre Completo" name="full_name" value={formData.full_name} onChange={handleChange} />
          {!editingId && <TextField fullWidth margin="normal" label="Contraseña" type="password" name="password" value={formData.password} onChange={handleChange} />}
          <Select fullWidth margin="normal" name="role" value={formData.role} onChange={handleChange}>
            <MenuItem value="admin">Admin</MenuItem>
            <MenuItem value="superadmin">Superadmin</MenuItem>
          </Select>
          <FormControlLabel control={<Switch checked={formData.is_active} onChange={handleChange} name="is_active" />} label="Activo" />
          <FormControlLabel control={<Switch checked={formData.otp_enabled} onChange={handleChange} name="otp_enabled" />} label="OTP Habilitado" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>{editingId ? 'Actualizar' : 'Crear'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
